import { LitElement, html, svg, css, TemplateResult, PropertyValues } from 'lit';
import { property, customElement, state, query } from 'lit/decorators.js';
import { ButtonColor } from '../../gzIconButton/types';
import { styleMap } from 'lit/directives/style-map.js';
import IconSet from '../GzIconSet';
import { IconSets } from '../../../theme';

/**
 * @summary This component can be used to display SVG icons
 * @tag gz-icon
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-icon')
export class GzIconWebComponent extends LitElement {
  /**
   * @type {string} name - this value needs to match the symbol id of the icon, i.e. 'ds-icon-clear', 'ds-icon-cancel' etc.
   * The component provides a preset SVG icon set. For custom icons, a valid SVG icon can be placed inside the <gz-icon> tags.
   * The full list of preset icons can be found here:
   * https://rcd-static.npunix.bitdefender.biz/storybooks/nirvana/latest/?path=/story/design-system--icons
   */
  @property({ type: String, attribute: true, reflect: true })
  name = '';

  /**
   * @type {string} color - the property accepts values from the ButtonColor enum or a custom HEX code or CSS color variable
   * Alternately, the icon color can be overridden in CSS by setting a custom fill color on the 'svgIcon' part, i.e.:
   * gz-icon::part(svgIcon) {
   *   fill: var(--red-alert-chart-700-critical);
   * }
   */
  @property({ type: String, attribute: true, reflect: true })
  color: ButtonColor = ButtonColor.PRIMARY;

  /**
   * @type {number} size
   */
  @property({ type: Number, attribute: true, reflect: true })
  size = 20;

  /**
   * This property decides if the component should load a custom icon set.
   */
  @property({ type: String, attribute: true, reflect: true })
  customIconSet?: keyof typeof IconSets = undefined;

  @query('slot[name="custom-svg"]')
  private svgSlot: HTMLSlotElement;

  @state()
  colors: Map<ButtonColor, string> = new Map<ButtonColor, string>([
    [ButtonColor.PRIMARY, 'var(--blue-layout-400-primary)'],
    [ButtonColor.PRIMARY_ACTIVE, 'var(--blue-layout-300-active)'],
    [ButtonColor.SECONDARY, 'var(--chart-302-icon-buttons-titles)'],
    [ButtonColor.SUCCESS, 'var(--green-layout-100-primary-green)'],
    [ButtonColor.NEGATIVE, 'var(--red-layout-300-primary)'],
    [ButtonColor.WARNING, 'var(--orange-layout-050-warning)'],
    [ButtonColor.WHITE, 'var(--white-layout-300-primary)'],
  ]);

  /**
   * Internal property used for keeping the reference of the 'iconSet' singleton instance
   * @private
   */
  @state()
  private iconSetInstance: IconSet;

  /**
   * Internal property used for storing the SVG node element if found in the preset icons map
   * @private
   */
  @property()
  private iconSvg: Element;

  constructor() {
    super();
    this.iconSetInstance = IconSet.getInstance();
  }

  private get svgSlottedContent(): SVGElement | null {
    const slotNodes: Node[] = this.svgSlot.assignedNodes();
    if (slotNodes.length > 0 && slotNodes[0] instanceof SVGElement) {
      return slotNodes[0];
    }

    return null;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);

    if (this.customIconSet) {
      this.setCustomIconSet(IconSets[this.customIconSet]);
    }
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (_changedProperties.has('size') || _changedProperties.has('color')) {
      if (this.svgSlottedContent) {
        this.updateSlottedContentStyle(this.svgSlottedContent as SVGElement);
      }
    }
  }

  //this can be used to set a custom icon set
  public setCustomIconSet(iconSet: TemplateResult): void {
    this.iconSetInstance.loadCustomIconSet(iconSet);
  }

  static get styles() {
    return css`
      :host {
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box !important;
      }

      svg {
        display: block;
      }

      .svg-container {
        display: inline-block;
        box-sizing: content-box !important;
      }
    `;
  }

  private updateSlottedContentStyle(svgElement: SVGElement): void {
    svgElement.setAttribute('fill', this.colors.get(this.color) || this.color || 'inherit');
    svgElement.setAttribute('width', `${this.size}`);
    svgElement.setAttribute('height', `${this.size}`);
  }

  private onSlotChange(): void {
    if (this.svgSlottedContent) {
      this.updateSlottedContentStyle(this.svgSlottedContent);
    }
  }

  private renderIconSvg(): TemplateResult {
    const icon = this.iconSetInstance.getIcon(this.name);

    if (icon && !icon.isEqualNode(this.iconSvg)) {
      this.iconSvg = icon.cloneNode(true) as Element;
    }
    const containerStyles = {
      width: `${this.size}px`,
      height: `${this.size}px`,
    };
    const svgContent = svg`<defs>${this.iconSvg}</defs>`;
    return html`
      <svg
        aria-hidden="true"
        style="position: absolute; width: 0; height: 0; overflow: hidden;"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
      >
        ${svgContent}
      </svg>
      <span id="container" class="svg-container" part="svgContainer" style=${styleMap(containerStyles)}>
        <svg fill="${this.colors.get(this.color) || this.color}" width="100%" height="100%" part="highlightable svgIcon">
          <use href="#${this.name}"></use>
        </svg>
      </span>
    `;
  }

  render() {
    return html`
      ${this.name && this.renderIconSvg()}
      <slot name="custom-svg" @slotchange="${this.onSlotChange}" part="svgIcon"></slot>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-icon': GzIconWebComponent;
  }
}
